import { Button, Drawer } from '@mui/material';
import { Chain } from '@ankr.com/chains-list';
import { LoadingButton } from '@ankr.com/ui';

import { AccountPermission } from 'modules/permissions/constants';
import { Checkbox } from 'modules/common/components/Checkbox';
import { GuardAccountPermission } from 'modules/account/components/GuardAccountPermission';
import { IProjectWithBlockchains } from 'domains/projects/types';
import { JWT } from 'domains/jwtToken/types';
import { Placeholder } from 'modules/common/components/Placeholder';
import { useSubchainLabels } from 'modules/chains/hooks/useSubchainLabels';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { ChainProjectAccordion } from './components/ChainProjectAccordion';
import { ChainProjectSingleItem } from './components/ChainProjectSingleItem/ChainProjectSingleItem';
import { ChainProjectsSidebarHeader } from './components/ChainProjectsSidebarHeader';
import { ProjectsSkeleton } from './components/ProjectsSkeleton';
import { chainProjectItemTranslation } from './translation';
import { useChainProjectsAccordionExpandedId } from './hooks/useChainProjectsAccordionExpandedId';
import { useChainProjectsSidebar } from './hooks/useChainProjectsSidebar';
import { useChainProjectsSidebarStyles } from './useChainProjectsSidebarStyles';
import { useProjectSubchains } from './hooks/useProjectSubchains';

interface IChainProjectsSidebarProps {
  chain: Chain;
  isLoading?: boolean;
  isOpenedAddToProjectsSidebar: boolean;
  onCloseAddToProjectsSidebar: () => void;
  projects: JWT[];
  projectsWithBlockchains: IProjectWithBlockchains[];
}

export const ChainProjectsSidebar = ({
  chain,
  isLoading = false,
  isOpenedAddToProjectsSidebar,
  onCloseAddToProjectsSidebar,
  projects,
  projectsWithBlockchains,
}: IChainProjectsSidebarProps) => {
  const { selectedSubchains, setSelectedSubchains } = useProjectSubchains({
    projectsWithBlockchains,
  });

  const { expandedId, onToggleAccordion, setExpandedId } =
    useChainProjectsAccordionExpandedId();

  const {
    handleAllChange,
    handleProjectChange,
    hasDisabledChildren,
    isAllIndeterminate,
    isAllSelected,
    isLoadingAddChainsToProject,
    isLoadingAddChainsToProjects,
    onConfirm,
  } = useChainProjectsSidebar({
    chain,
    onCloseAddToProjectsSidebar,
    selectedSubchains,
    setExpandedId,
    setSelectedSubchains,
  });

  const subchainLabels = useSubchainLabels({ chain });

  const { classes } = useChainProjectsSidebarStyles();

  const { keys, t } = useTranslation(chainProjectItemTranslation);

  const isConfirming =
    isLoadingAddChainsToProject || isLoadingAddChainsToProjects;

  return (
    <GuardAccountPermission permission={AccountPermission.JwtManagerRead}>
      <Drawer
        anchor="right"
        onClick={event => event.stopPropagation()}
        onClose={onCloseAddToProjectsSidebar}
        open={isOpenedAddToProjectsSidebar}
        classes={{
          paper: classes.addToProjectsDialogPaper,
        }}
      >
        <ChainProjectsSidebarHeader
          chain={chain}
          subchainLabels={subchainLabels}
          onCloseAddToProjectsSidebar={onCloseAddToProjectsSidebar}
          projectsCount={projects.length}
        />
        <Placeholder
          hasPlaceholder={isLoading}
          placeholder={<ProjectsSkeleton />}
        >
          {projects.length === 1 && (
            <ChainProjectSingleItem
              chain={chain}
              handleProjectChange={handleProjectChange}
              isLoadingAddChainsToProject={isLoadingAddChainsToProject}
              project={projects[0]}
              selectedProjectsSubchains={selectedSubchains}
              setSelectedSubchains={setSelectedSubchains}
              onToggleAccordion={onToggleAccordion}
              expandedId={expandedId}
            />
          )}
          {projects.length > 1 && (
            <>
              <Checkbox
                classNameRoot={classes.selectAllCheckbox}
                hasBorderBottom
                hasPadding
                isChecked={isAllSelected}
                isDisabled={isConfirming || hasDisabledChildren}
                isIndeterminate={isAllIndeterminate}
                label={t(keys.selectAll)}
                onChange={handleAllChange}
              />
              {projects.map(project => (
                <ChainProjectAccordion
                  chain={chain}
                  expandedId={expandedId}
                  handleProjectChange={handleProjectChange}
                  isLoadingAddChainsToProject={isLoadingAddChainsToProject}
                  isLoadingAddChainsToProjects={isLoadingAddChainsToProjects}
                  key={project.userEndpointToken}
                  onToggleAccordion={onToggleAccordion}
                  project={project}
                  selectedProjectsSubchains={selectedSubchains}
                  setSelectedSubchains={setSelectedSubchains}
                />
              ))}
            </>
          )}
          <div className={classes.chainProjectsActions}>
            <LoadingButton
              disabled={isConfirming}
              fullWidth
              loading={isConfirming}
              onClick={onConfirm}
              size="large"
            >
              {t(keys.confirm)}
            </LoadingButton>
            <Button
              size="large"
              fullWidth
              variant="outlined"
              onClick={onCloseAddToProjectsSidebar}
            >
              {t(keys.cancel)}
            </Button>
          </div>
        </Placeholder>
      </Drawer>
    </GuardAccountPermission>
  );
};
